import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Code, 
  Dna, 
  Palette, 
  Cpu, 
  ChevronDown, 
  Menu, 
  X, 
  Send, 
  ArrowRight,
  CheckCircle,
  Users,
  Briefcase,
  Target
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Logo - new version
const LOGO_URL = "https://customer-assets.emergentagent.com/job_expertise-dev/artifacts/naed9orx_WhatsApp%20Image%202026-03-12%20at%2004.20.12.jpeg";

// Fresh unique images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1768330187404-59e46cf222c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwYWJzdHJhY3QlMjBkYXRhJTIwdmlzdWFsaXphdGlvbiUyMHRlY2hub2xvZ3klMjBibHVlfGVufDB8fHx8MTc3MzU2MTg2Mnww&ixlib=rb-4.1.0&q=85",
  software: "https://images.unsplash.com/photo-1607971422532-73f9d45d7a47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxkZXZlbG9wZXIlMjBjb2RpbmclMjBsYXB0b3AlMjBzY3JlZW58ZW58MHx8fHwxNzczMjg1MDk5fDA&ixlib=rb-4.1.0&q=85",
  bioinformatics: "https://images.unsplash.com/photo-1707944746058-4da338d0f827?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxsYWJvcmF0b3J5JTIwcmVzZWFyY2glMjBzY2llbnRpc3R8ZW58MHx8fHwxNzczMjg1MDk5fDA&ixlib=rb-4.1.0&q=85",
  uiux: "https://images.unsplash.com/photo-1735200896762-740a563715e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxkZXNpZ25lciUyMHRhYmxldCUyMGRpZ2l0YWwlMjBhcnR3b3JrfGVufDB8fHx8MTc3MzI4NTEwMHww&ixlib=rb-4.1.0&q=85",
  embedded: "https://customer-assets.emergentagent.com/job_expertise-dev/artifacts/72tjjzlq_image.png",
  about: "https://images.unsplash.com/photo-1733222765234-08c33d436c50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxhdWRpZW5jZSUyMHdvcmtzaG9wJTIwc2VtaW5hciUyMGZyb20lMjBiZWhpbmQlMjBkaXZlcnNlJTIwcGVvcGxlfGVufDB8fHx8MTc3MzU2MTI3NHww&ixlib=rb-4.1.0&q=85"
};

// Custom Logo Component - U and D with amber background - ULTRA BRIGHT with vignette
const LogoIcon = ({ size = 64, className = "" }) => (
  <div 
    className={`relative rounded-xl overflow-hidden flex items-center justify-center ${className}`}
    style={{ 
      width: size, 
      height: size,
      background: "radial-gradient(circle at center, #ffca28 0%, #ffc107 40%, #e6a000 70%, #cc8800 100%)",
      filter: "saturate(1.3) brightness(1.15)"
    }}
  >
    <svg 
      viewBox="0 0 60 60" 
      width={size * 0.85}
      height={size * 0.85}
    >
      {/* U shape - bright gold outer frame */}
      <path 
        d="M5 5 L5 40 C5 52 15 55 30 55 C45 55 55 52 55 40 L55 5 L48 5 L48 40 C48 48 42 50 30 50 C18 50 12 48 12 40 L12 5 Z"
        fill="#e6a000"
        stroke="#cc8800"
        strokeWidth="1"
      />
      {/* D shape group - animated with robot movement - SLOWER */}
      <motion.g
        animate={{ 
          y: [0, -3, -3, 0, 3, 3, 0]
        }}
        transition={{ 
          duration: 2.5,
          repeat: Infinity,
          ease: "steps(7)",
          times: [0, 0.15, 0.3, 0.5, 0.65, 0.85, 1]
        }}
      >
        {/* D shape - navy blue center */}
        <rect x="18" y="15" width="24" height="30" rx="3" fill="#1e3a5f" />
        <path 
          d="M22 19 L22 41 L32 41 C40 41 40 19 32 19 Z"
          fill="#1e3a5f"
        />
        {/* D inner cutout - bright amber */}
        <path 
          d="M26 24 L32 24 C36 24 36 36 32 36 L26 36 Z"
          fill="#ffca28"
        />
      </motion.g>
    </svg>
  </div>
);

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#expertise", label: "Expertise" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
    >
      {/* Amber gradient sprinkle across top - MORE VISIBLE */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400" />
      <motion.div 
        className="absolute top-0 left-0 w-48 h-48 bg-gradient-to-br from-amber-400/40 to-transparent rounded-full blur-3xl -translate-x-1/4 -translate-y-1/2"
        animate={{ opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div 
        className="absolute top-0 left-1/4 w-32 h-32 bg-gradient-to-b from-orange-400/30 to-transparent rounded-full blur-2xl -translate-y-1/3"
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 4, repeat: Infinity, delay: 1 }}
      />
      <motion.div 
        className="absolute top-0 right-1/4 w-40 h-40 bg-gradient-to-b from-amber-500/35 to-transparent rounded-full blur-2xl -translate-y-1/3"
        animate={{ opacity: [0.35, 0.55, 0.35] }}
        transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
      />
      <motion.div 
        className="absolute top-0 right-0 w-56 h-56 bg-gradient-to-bl from-amber-500/40 to-transparent rounded-full blur-3xl translate-x-1/4 -translate-y-1/2"
        animate={{ opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
      />
      
      <div className="w-full px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo with breathing animation - Custom U+D icon */}
          <a href="#" className="logo-hover flex items-center gap-3" data-testid="logo-link">
            <motion.div 
              className="rounded-xl overflow-hidden"
              animate={{ 
                scale: [1, 1.06, 1],
                boxShadow: [
                  "0 0 25px rgba(245, 158, 11, 0.6), 0 0 50px rgba(245, 158, 11, 0.3)",
                  "0 0 40px rgba(245, 158, 11, 0.8), 0 0 80px rgba(245, 158, 11, 0.5)",
                  "0 0 25px rgba(245, 158, 11, 0.6), 0 0 50px rgba(245, 158, 11, 0.3)"
                ]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <LogoIcon size={56} />
            </motion.div>
            <span className="font-bold text-xl text-slate-900 hidden sm:block tracking-wide">
              UNDERBAR
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="nav-link font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <Button 
              className="btn-primary bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 shadow-lg shadow-slate-900/20"
              onClick={(e) => scrollToSection(e, "#contact")}
              data-testid="nav-cta-button"
            >
              Get Advice
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="py-2 font-medium text-slate-700 hover:text-slate-900"
                >
                  {link.label}
                </a>
              ))}
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full w-full"
                onClick={(e) => scrollToSection(e, "#contact")}
              >
                Get Advice
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center pt-20 grid-pattern overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated background glows */}
      <motion.div 
        className="hero-glow absolute top-1/4 right-0 translate-x-1/3"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{ duration: 5, repeat: Infinity }}
      />
      <motion.div 
        className="hero-glow absolute bottom-0 left-0 -translate-x-1/3"
        animate={{ 
          scale: [1.1, 1, 1.1],
          opacity: [0.4, 0.6, 0.4]
        }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      
      {/* Floating particles */}
      <motion.div
        className="absolute top-1/3 left-1/4 w-3 h-3 bg-amber-400 rounded-full"
        animate={{ 
          y: [0, -30, 0],
          opacity: [0.3, 0.7, 0.3]
        }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 right-1/3 w-2 h-2 bg-slate-400 rounded-full"
        animate={{ 
          y: [0, -20, 0],
          opacity: [0.2, 0.5, 0.2]
        }}
        transition={{ duration: 3, repeat: Infinity, delay: 1 }}
      />
      <motion.div
        className="absolute bottom-1/3 left-1/3 w-4 h-4 bg-amber-300 rounded-full"
        animate={{ 
          y: [0, -25, 0],
          opacity: [0.2, 0.4, 0.2]
        }}
        transition={{ duration: 5, repeat: Infinity, delay: 2 }}
      />
      
      <div className="w-full px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <motion.span 
              className="inline-block text-sm font-semibold uppercase tracking-widest text-amber-600 mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              &nbsp;
            </motion.span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[0.9] text-slate-900 mb-6">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="block"
              >
                Defining the
              </motion.span>
              <motion.span 
                className="text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600 block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                solution.
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="block"
              >
                Delivering the result
              </motion.span>
            </h1>
            <motion.p 
              className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              We provide expert consulting services in software development, 
              bioinformatics, UI/UX design, and embedded systems.
            </motion.p>
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Button 
                className="btn-primary bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-slate-900/20"
                onClick={() => document.querySelector("#services").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-explore-button"
              >
                Explore Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-slate-200 hover:bg-slate-50"
                onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-contact-button"
              >
                Contact Us
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative w-full h-full">
              <motion.img 
                src={IMAGES.hero}
                alt="Technology Abstract"
                className="rounded-2xl shadow-2xl shadow-slate-900/10 w-full h-[500px] object-cover"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
              {/* Floating badge */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                whileHover={{ scale: 1.05 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <motion.div 
                    className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center"
                    animate={{ 
                      scale: [1, 1.1, 1],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <CheckCircle className="h-6 w-6 text-amber-600" />
                  </motion.div>
                  <div>
                    <p className="font-bold text-slate-900">Expert Solutions</p>
                    <p className="text-sm text-slate-500">Trusted by businesses</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Service Helix Component
const ServiceHelix = () => {
  const [activeService, setActiveService] = useState(null);

  const services = [
    {
      id: "software",
      icon: Code,
      title: "Software Design & Development",
      description: "Custom software solutions tailored to your business needs. From architecture design to full-stack development.",
      details: ["Web Applications", "Mobile Solutions", "API Development", "Cloud Architecture"],
      image: IMAGES.software
    },
    {
      id: "bioinformatics",
      icon: Dna,
      title: "Bioinformatics",
      description: "Advanced computational biology solutions for research and healthcare organizations.",
      details: ["Sequence Analysis", "Genomics Data", "Molecular Modeling", "Data Pipelines"],
      image: IMAGES.bioinformatics
    },
    {
      id: "uiux",
      icon: Palette,
      title: "UI/UX Design",
      description: "User-centered design that creates intuitive and engaging digital experiences.",
      details: ["User Research", "Wireframing", "Prototyping", "Design Systems"],
      image: IMAGES.uiux
    },
    {
      id: "embedded",
      icon: Cpu,
      title: "Embedded Systems",
      description: "Hardware-software integration for IoT, automotive, and industrial applications.",
      details: ["Firmware Development", "IoT Solutions", "Automotive Systems", "Hardware Integration"],
      image: IMAGES.embedded
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-slate-50/50" data-testid="services-section">
      <div className="w-full px-6 md:px-12 lg:px-16 max-w-screen-2xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="text-sm font-semibold uppercase tracking-widest text-amber-600 mb-4 block">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Consulting & Expertise Package
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl">
            A comprehensive suite of services designed to transform your ideas into reality.
          </p>
        </motion.div>

        {/* Service Helix Grid */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Service List */}
          <div className="lg:col-span-5 space-y-4">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`service-helix-item rounded-xl p-6 cursor-pointer ${
                  activeService === service.id ? "active" : ""
                }`}
                onClick={() => setActiveService(activeService === service.id ? null : service.id)}
                data-testid={`service-${service.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <service.icon className="h-6 w-6 text-slate-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-lg text-slate-900">{service.title}</h3>
                      <ChevronDown 
                        className={`h-5 w-5 text-slate-400 transition-transform ${
                          activeService === service.id ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <AnimatePresence>
                      {activeService === service.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4"
                        >
                          <p className="text-slate-600 mb-4">{service.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {service.details.map((detail) => (
                              <span 
                                key={detail}
                                className="text-xs font-medium bg-amber-100 text-amber-700 px-3 py-1 rounded-full"
                              >
                                {detail}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Service Image Display */}
          <div className="lg:col-span-7 relative">
            <div className="sticky top-32 rounded-2xl overflow-hidden bg-slate-100 aspect-[4/3]">
              <AnimatePresence mode="wait">
                {activeService ? (
                  <motion.img
                    key={activeService}
                    src={services.find(s => s.id === activeService)?.image}
                    alt={services.find(s => s.id === activeService)?.title}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                    className="w-full h-full object-cover"
                    data-testid="service-image"
                  />
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full flex items-center justify-center"
                  >
                    <div className="text-center">
                      <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 font-medium">Select a service to learn more</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Skills Section - Modern & Animated
const SkillsSection = () => {
  const skills = [
    { 
      name: "Software Development", 
      icon: Code,
      description: "Full-stack solutions from concept to deployment",
      color: "from-blue-500 to-cyan-500"
    },
    { 
      name: "Bioinformatics", 
      icon: Dna,
      description: "Data pipelines and computational biology",
      color: "from-emerald-500 to-teal-500"
    },
    { 
      name: "UI/UX Design", 
      icon: Palette,
      description: "User-centered interfaces that delight",
      color: "from-purple-500 to-pink-500"
    },
    { 
      name: "Embedded Systems", 
      icon: Cpu,
      description: "Hardware-software integration expertise",
      color: "from-orange-500 to-amber-500"
    },
    { 
      name: "Project Management", 
      icon: Target,
      description: "Agile delivery with precision",
      color: "from-slate-600 to-slate-800"
    },
  ];

  return (
    <section 
      id="expertise" 
      className="py-24 md:py-32 bg-white relative overflow-hidden"
      data-testid="expertise-section"
    >
      {/* Animated background blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-200/30 rounded-full filter blur-3xl morphing-blob" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/20 rounded-full filter blur-3xl morphing-blob" style={{ animationDelay: '4s' }} />
      
      <div className="w-full px-6 md:px-12 lg:px-16 max-w-screen-2xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span 
            className="text-sm font-semibold uppercase tracking-widest text-amber-600 mb-4 block"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Expertise
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            Our Core Competencies
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
            With years of experience across multiple domains, we bring deep expertise 
            to every project. Our team combines technical excellence with strategic thinking.
          </p>
          
          {/* Animated 100% Client Satisfaction Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50 border border-amber-200 rounded-full px-6 py-3 relative overflow-hidden"
          >
            <div className="absolute inset-0 shimmer" />
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <CheckCircle className="h-6 w-6 text-amber-600" />
            </motion.div>
            <span className="text-2xl font-bold text-slate-900">100%</span>
            <span className="text-slate-600 font-medium">Client Satisfaction</span>
          </motion.div>
        </motion.div>

        {/* Skills Grid - Animated Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {skills.map((skill, index) => (
            <motion.div
              key={skill.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
              whileHover={{ 
                y: -8, 
                transition: { duration: 0.2 } 
              }}
              className="skill-card group relative bg-white border border-slate-200 rounded-2xl p-6 cursor-pointer"
              data-testid={`skill-${skill.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Animated gradient border on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-sm" />
              <div className="absolute inset-[1px] rounded-2xl bg-white -z-10" />
              
              {/* Floating Icon */}
              <motion.div 
                className={`w-14 h-14 bg-gradient-to-br ${skill.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}
                whileHover={{ 
                  scale: 1.1,
                  rotate: [0, -5, 5, 0],
                }}
                transition={{ duration: 0.3 }}
              >
                <skill.icon className="h-7 w-7 text-white" />
              </motion.div>
              
              {/* Content */}
              <h3 className="font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">
                {skill.name}
              </h3>
              <p className="text-sm text-slate-500">{skill.description}</p>
              
              {/* Animated bottom accent */}
              <motion.div 
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-b-2xl"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    {
      icon: Users,
      title: "Expert Team",
      description: "Skilled professionals with deep domain expertise"
    },
    {
      icon: Target,
      title: "Focused Approach",
      description: "Goal-oriented methodology for optimal results"
    },
    {
      icon: Briefcase,
      title: "Business Acumen",
      description: "Understanding your business needs first"
    }
  ];

  return (
    <section id="about" className="py-24 md:py-32 bg-slate-50/50" data-testid="about-section">
      <div className="w-full px-6 md:px-12 lg:px-16 max-w-screen-2xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src={IMAGES.about}
              alt="Team collaboration"
              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
            />
            {/* Accent element */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-500/20 rounded-2xl -z-10" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-semibold uppercase tracking-widest text-amber-600 mb-4 block">
              About Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              The Intersection of Biology and Bytecode
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Underbar Solutions bridges the gap between cutting-edge technology and practical 
              business needs. We specialize in delivering expert consulting services that transform 
              complex challenges into elegant solutions.
            </p>

            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Message sent successfully! We'll get back to you soon.");
        setFormData({ name: "", email: "", company: "", message: "" });
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white relative overflow-hidden" data-testid="contact-section">
      {/* Sun and sunlight effects */}
      <div className="absolute top-0 right-0 w-96 h-96 -translate-y-1/2 translate-x-1/4">
        {/* Sun circle */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full"
          style={{
            background: "radial-gradient(circle, #ffd54f 0%, #ffb300 50%, #ff8f00 100%)",
            boxShadow: "0 0 60px 20px rgba(255, 193, 7, 0.4), 0 0 100px 40px rgba(255, 152, 0, 0.2)"
          }}
          animate={{
            scale: [1, 1.05, 1],
            boxShadow: [
              "0 0 60px 20px rgba(255, 193, 7, 0.4), 0 0 100px 40px rgba(255, 152, 0, 0.2)",
              "0 0 80px 30px rgba(255, 193, 7, 0.5), 0 0 120px 50px rgba(255, 152, 0, 0.3)",
              "0 0 60px 20px rgba(255, 193, 7, 0.4), 0 0 100px 40px rgba(255, 152, 0, 0.2)"
            ]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Sun rays */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 h-1 bg-gradient-to-r from-amber-400/60 to-transparent"
            style={{
              width: "150px",
              transformOrigin: "left center",
              transform: `rotate(${i * 30}deg)`,
            }}
            animate={{
              opacity: [0.3, 0.7, 0.3],
              scaleX: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              delay: i * 0.1,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
      
      {/* Warm light overlay */}
      <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-amber-100/40 via-transparent to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-full blur-3xl" />

      <div className="w-full px-6 md:px-12 lg:px-16 max-w-screen-2xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-semibold uppercase tracking-widest text-amber-600 mb-4 block">
              Get in Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Let's unlock what's next
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Ready to transform your ideas into reality? We're here to help. 
              Reach out and let's discuss how we can work together.
            </p>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6 contact-form" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="floating-label-input">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    data-testid="contact-name-input"
                  />
                  <label htmlFor="name">Your Name</label>
                </div>
                <div className="floating-label-input">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    data-testid="contact-email-input"
                  />
                  <label htmlFor="email">Email Address</label>
                </div>
              </div>
              <div className="floating-label-input">
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder=" "
                  data-testid="contact-company-input"
                />
                <label htmlFor="company">Company Name</label>
              </div>
              <div className="floating-label-input">
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder=" "
                  rows={5}
                  required
                  className="resize-none"
                  data-testid="contact-message-input"
                />
                <label htmlFor="message">Your Message</label>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 text-lg shadow-lg shadow-slate-900/20"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <Send className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer with two robots that meet in the middle
const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const cycleDuration = isMobile ? 10 : 20;
  const robotSize = isMobile ? 30 : 40;

  const RobotSVG = ({ isLeftRobot }) => (
    <svg 
      width={robotSize} 
      height={robotSize} 
      viewBox="0 0 40 40"
      style={{ 
        transform: isLeftRobot ? 'scaleX(1)' : 'scaleX(-1)',
      }}
    >
      {/* Body */}
      <g>
        {/* Head */}
        <rect x="12" y="4" width="16" height="12" rx="2" fill="#fbbf24" />
        {/* Eyes */}
        <circle cx="17" cy="10" r="2" fill="#1e293b" />
        <circle cx="23" cy="10" r="2" fill="#1e293b" />
        {/* Antenna */}
        <line x1="20" y1="4" x2="20" y2="0" stroke="#fbbf24" strokeWidth="2" />
        <circle cx="20" cy="0" r="2" fill="#f59e0b" />
        {/* Body */}
        <rect x="10" y="16" width="20" height="14" rx="2" fill="#fbbf24" />
        {/* Screen on body */}
        <rect x="14" y="19" width="12" height="6" rx="1" fill="#1e293b" />
        <rect x="16" y="21" width="3" height="2" fill="#4ade80" />
      </g>
      {/* Legs */}
      <rect x="12" y="30" width="4" height="8" rx="1" fill="#e6a000" />
      <rect x="24" y="30" width="4" height="8" rx="1" fill="#e6a000" />
    </svg>
  );

  return (
    <footer className="bg-slate-900 text-white py-8 relative overflow-hidden" data-testid="footer">
      {/* Robot 1: A(left) → B(center) SPARK → immediately back to A */}
      <motion.div
        className="absolute bottom-2 flex items-end"
        animate={{
          left: ["5%", "calc(50% - 30px)", "calc(50% - 30px)", "5%"]
        }}
        transition={{
          duration: cycleDuration,
          repeat: Infinity,
          ease: "linear",
          times: [0, 0.45, 0.55, 1]
        }}
      >
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 0.3, repeat: Infinity }}
        >
          <motion.div
            animate={{ scaleX: [1, 1, -1, -1] }}
            transition={{
              duration: cycleDuration,
              repeat: Infinity,
              times: [0, 0.50, 0.51, 1]
            }}
          >
            <RobotSVG isLeftRobot={true} />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Robot 2: A(right) → B(center) SPARK → immediately back to A */}
      <motion.div
        className="absolute bottom-2 flex items-end"
        animate={{
          right: ["5%", "calc(50% - 30px)", "calc(50% - 30px)", "5%"]
        }}
        transition={{
          duration: cycleDuration,
          repeat: Infinity,
          ease: "linear",
          times: [0, 0.45, 0.55, 1]
        }}
      >
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 0.3, repeat: Infinity }}
        >
          <motion.div
            animate={{ scaleX: [-1, -1, 1, 1] }}
            transition={{
              duration: cycleDuration,
              repeat: Infinity,
              times: [0, 0.50, 0.51, 1]
            }}
          >
            <RobotSVG isLeftRobot={true} />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* SPARK at B (center) - quick shock ⚡️ */}
      <motion.div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none z-20"
        animate={{
          opacity: [0, 0, 1, 0, 0],
          scale: [0, 0, 1.5, 0, 0]
        }}
        transition={{
          duration: cycleDuration,
          repeat: Infinity,
          times: [0, 0.48, 0.5, 0.52, 1]
        }}
      >
        {/* Electric spark SVG - BIGGER */}
        <svg width="80" height="60" viewBox="0 0 80 60">
          {/* Main lightning bolt */}
          <motion.path
            d="M40 0 L32 20 L42 20 L28 60 L38 32 L28 32 L40 0"
            fill="#ffeb3b"
            stroke="#fff"
            strokeWidth="2"
            animate={{
              fill: ["#ffeb3b", "#ffffff", "#00bcd4", "#ffeb3b"],
            }}
            transition={{
              duration: 0.1,
              repeat: Infinity,
            }}
          />
          {/* Glow effect */}
          <motion.ellipse
            cx="40" cy="30" rx="30" ry="25"
            fill="rgba(255, 235, 59, 0.3)"
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.2, 1]
            }}
            transition={{ duration: 0.15, repeat: Infinity }}
          />
          {/* Spark particles */}
          <motion.circle cx="15" cy="25" r="4" fill="#00bcd4"
            animate={{ opacity: [0, 1, 0], x: [-5, 0, 5] }}
            transition={{ duration: 0.2, repeat: Infinity }}
          />
          <motion.circle cx="65" cy="25" r="4" fill="#ffeb3b"
            animate={{ opacity: [0, 1, 0], x: [5, 0, -5] }}
            transition={{ duration: 0.15, repeat: Infinity }}
          />
          <motion.circle cx="25" cy="45" r="3" fill="#fff"
            animate={{ opacity: [0, 1, 0], y: [0, 5, 10] }}
            transition={{ duration: 0.18, repeat: Infinity }}
          />
          <motion.circle cx="55" cy="45" r="3" fill="#00bcd4"
            animate={{ opacity: [0, 1, 0], y: [0, 5, 10] }}
            transition={{ duration: 0.2, repeat: Infinity }}
          />
          <motion.circle cx="35" cy="10" r="3" fill="#ffeb3b"
            animate={{ opacity: [0, 1, 0], y: [0, -5, -10] }}
            transition={{ duration: 0.12, repeat: Infinity }}
          />
          <motion.circle cx="50" cy="15" r="2" fill="#fff"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.1, repeat: Infinity }}
          />
          {/* Zigzag sparks */}
          <motion.path
            d="M5 30 L12 22 L8 35 L15 28"
            stroke="#00bcd4"
            strokeWidth="3"
            fill="none"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.12, repeat: Infinity }}
          />
          <motion.path
            d="M75 30 L68 22 L72 35 L65 28"
            stroke="#ffeb3b"
            strokeWidth="3"
            fill="none"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.15, repeat: Infinity }}
          />
        </svg>
      </motion.div>

      <div className="w-full px-6 md:px-12 lg:px-16 max-w-screen-2xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <motion.div 
            className="flex items-center gap-3 md:mr-auto"
            whileHover={{ scale: 1.02 }}
          >
            <motion.div 
              className="rounded-lg overflow-hidden"
              animate={{ 
                boxShadow: [
                  "0 0 15px rgba(245, 158, 11, 0.4)",
                  "0 0 25px rgba(245, 158, 11, 0.6)",
                  "0 0 15px rgba(245, 158, 11, 0.4)"
                ]
              }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              <LogoIcon size={44} />
            </motion.div>
          </motion.div>
          <p className="text-slate-400 text-sm">© 2026 Underbar Solutions. All Rights Reserved.</p>
          <div className="flex gap-6">
            <a href="#services" className="text-slate-400 hover:text-amber-400 transition-colors">Services</a>
            <a href="#expertise" className="text-slate-400 hover:text-amber-400 transition-colors">Expertise</a>
            <a href="#contact" className="text-slate-400 hover:text-amber-400 transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <ServiceHelix />
      <SkillsSection />
      <AboutSection />
      <ContactSection />
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
